import { useEffect, useMemo, useState } from 'react';
import { useAuth, apiClient, axiosClient } from '../../auth-lib';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { Button, Card, StatusPill, EmptyState, Skeleton, useToast } from '../../design-system';
import '../styles/AdminDashboard.css';

const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] } },
};

const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString();
};

const ACTION_VARIANT = {
  created: 'success',
  approved: 'success',
  restored: 'success',
  reopened: 'info',
  updated: 'info',
  status_changed: 'info',
  deleted: 'danger',
  rejected: 'danger',
};

function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [stats, setStats] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Org/user counts come from the auth-service; business + catalog metrics
      // and the activity feed from the hrms backend. Each is settled
      // independently so one failing (e.g. no audit logs yet) never blanks the
      // whole dashboard.
      const [statsRes, orgsRes, platformRes, activityRes] = await Promise.allSettled([
        axiosClient.get('/organizations/stats'),
        axiosClient.get('/organizations'),
        apiClient.get('/api/admin/platform-stats'),
        apiClient.get('/api/audit-logs', { params: { limit: 8 } }),
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (orgsRes.status === 'fulfilled') setOrganizations(orgsRes.value.data);
      if (platformRes.status === 'fulfilled') setPlatform(platformRes.value.data);
      if (activityRes.status === 'fulfilled') setActivity(activityRes.value.data);
      if (statsRes.status === 'rejected' && orgsRes.status === 'rejected') {
        toast.danger('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const orgNameById = useMemo(() => {
    const map = {};
    organizations.forEach((o) => {
      map[o._id] = o.name;
    });
    return map;
  }, [organizations]);

  const orgStatusBreakdown = useMemo(() => {
    const counts = {};
    organizations.forEach((o) => {
      const s = o.subscription?.status || 'unknown';
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [organizations]);

  const kpis = [
    { label: 'Organizations', value: stats?.totalOrganizations ?? 0, sub: `${stats?.activeOrganizations ?? 0} active`, accent: 'accent' },
    { label: 'Users', value: stats?.totalUsers ?? 0, accent: 'info' },
    { label: 'Clients', value: platform?.clients.total ?? 0, sub: `${platform?.clients.active ?? 0} active`, accent: 'accent' },
    { label: 'Open Tasks', value: platform?.tasks.open ?? 0, sub: `${platform?.tasks.proposed ?? 0} awaiting approval`, accent: 'info' },
    { label: 'Compliance Due ≤30d', value: platform?.compliance.dueSoon ?? 0, accent: 'warning' },
    { label: 'Overdue Compliance', value: platform?.compliance.overdue ?? 0, accent: 'danger' },
  ];

  if (loading) {
    return (
      <AdminLayout title="System Dashboard" subtitle={`Welcome, ${user?.name} — Super Admin`}>
        <div className="stats-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <Skeleton height={32} width="50%" style={{ marginBottom: 8 }} />
              <Skeleton height={12} width="60%" />
            </Card>
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="System Dashboard" subtitle={`Welcome, ${user?.name} — Super Admin`}>
      <div className="admin-dashboard-page">
        <motion.div className="stats-grid" variants={containerVariants} initial="hidden" animate="visible">
          {kpis.map((s) => (
            <motion.div key={s.label} variants={itemVariants}>
              <Card className={`stat-card stat-card-${s.accent}`}>
                <div className="stat-number">{s.value}</div>
                <div className="stat-label">{s.label}</div>
                {s.sub && <div className="stat-sub">{s.sub}</div>}
              </Card>
            </motion.div>
          ))}
        </motion.div>

        <div className="admin-cards-grid">
          {/* Catalog health — ties to /admin/portals and /admin/services */}
          <Card>
            <Card.Header
              title="Platform Catalog"
              action={<button className="action-link" onClick={() => navigate('/admin/portals')}>Manage portals →</button>}
            />
            <div className="mini-list">
              <MiniRow label="Login portals" value={platform ? `${platform.portals.total}` : '—'} />
              <MiniRow
                label="Recipes verified"
                value={platform ? `${platform.portals.verified}/${platform.portals.withRecipe}` : '—'}
                pill={platform && platform.portals.unverified > 0
                  ? { variant: 'warning', label: `${platform.portals.unverified} to verify` }
                  : platform ? { variant: 'success', label: 'all verified' } : null}
              />
              <MiniRow label="Services in catalog" value={platform ? `${platform.services.total}` : '—'} />
              <MiniRow label="Stored credentials" value={platform ? `${platform.credentials.total}` : '—'} />
            </div>
          </Card>

          {/* Compliance load across every tenant */}
          <Card>
            <Card.Header title="Compliance Load" subtitle="Across all organizations" />
            <div className="mini-list">
              <MiniRow
                label="Overdue"
                value={platform ? platform.compliance.overdue : '—'}
                pill={platform && platform.compliance.overdue > 0 ? { variant: 'danger', label: 'action needed' } : null}
              />
              <MiniRow label="Due within 30 days" value={platform ? platform.compliance.dueSoon : '—'} />
              <MiniRow label="Total periods tracked" value={platform ? platform.compliance.total : '—'} />
            </div>
          </Card>

          {/* Organizations by plan (auth-service) */}
          {stats?.organizationsByPlan && (
            <Card>
              <Card.Header title="Organizations by Plan" />
              <div className="mini-list">
                {stats.organizationsByPlan.map((plan) => (
                  <MiniRow
                    key={plan._id}
                    label={<StatusPill variant="info" label={plan._id} />}
                    value={`${plan.count} org${plan.count !== 1 ? 's' : ''}`}
                  />
                ))}
              </div>
            </Card>
          )}

          {/* Subscription status mix */}
          {orgStatusBreakdown.length > 0 && (
            <Card>
              <Card.Header title="Subscription Status" />
              <div className="mini-list">
                {orgStatusBreakdown.map(([status, count]) => (
                  <MiniRow
                    key={status}
                    label={<StatusPill status={status} />}
                    value={`${count} org${count !== 1 ? 's' : ''}`}
                  />
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Recent cross-tenant activity */}
        <Card>
          <Card.Header
            title="Recent Activity"
            action={<button className="action-link" onClick={() => navigate('/admin/audit-logs')}>View all →</button>}
          />
          {activity.length === 0 ? (
            <EmptyState icon="📜" title="No activity yet" description="Actions across every organization will appear here." />
          ) : (
            <div className="activity-list">
              {activity.map((log) => (
                <div key={log._id} className="activity-row">
                  <StatusPill variant={ACTION_VARIANT[log.action] || 'neutral'} label={log.action.replace(/_/g, ' ')} />
                  <div className="activity-body">
                    <div className="activity-summary">{log.summary}</div>
                    <div className="activity-meta">
                      <strong>{log.actorName}</strong>
                      <span>·</span>
                      <span>{log.entityType}</span>
                      {orgNameById[log.organizationId] && (
                        <>
                          <span>·</span>
                          <span>{orgNameById[log.organizationId]}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="activity-time">{timeAgo(log.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <Card.Header
            title="Organizations"
            action={<Button variant="primary" onClick={() => navigate('/admin/organizations/create')}>+ New Organization</Button>}
          />
          {organizations.length === 0 ? (
            <EmptyState icon="🏢" title="No organizations yet" />
          ) : (
            <div className="organizations-list">
              {organizations.slice(0, 6).map((org) => (
                <div key={org._id} className="org-card">
                  <div className="org-header">
                    <h4>{org.name}</h4>
                    <StatusPill status={org.subscription.status} />
                  </div>
                  {org.description && <p className="org-description">{org.description}</p>}
                  <div className="org-info">
                    <span>Plan: <strong>{org.subscription.plan}</strong></span>
                    <span>Users: <strong>{org.userCount}</strong></span>
                    <span>Industry: <strong>{org.industry || 'N/A'}</strong></span>
                  </div>
                  <div className="org-actions">
                    <button className="action-link" onClick={() => navigate(`/admin/organizations/${org._id}`)}>View Details</button>
                    <button className="action-link" onClick={() => navigate(`/admin/organizations/${org._id}/edit`)}>Edit</button>
                  </div>
                </div>
              ))}
              {organizations.length > 6 && (
                <button className="action-link admin-viewall" onClick={() => navigate('/admin/organizations')}>
                  View all {organizations.length} organizations →
                </button>
              )}
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}

function MiniRow({ label, value, pill }) {
  return (
    <div className="mini-row">
      <span className="mini-row-label">{label}</span>
      <span className="mini-row-value">
        {pill && <StatusPill variant={pill.variant} label={pill.label} />}
        <strong>{value}</strong>
      </span>
    </div>
  );
}

export default AdminDashboard;
